import { useState } from "react";
import api from "@/lib/api";
import { trackLead } from "@/lib/googleTags";
import RichText from "@/components/RichText";
import { SmartLink } from "../components/Primitives";
import { BODY, BTN_FILL, CONTAINER, EYEBROW_LIGHT, LABEL, telHref } from "../tokens";

// « Nous contacter » (#contact) : colonne gauche = sur-titre, H2, paragraphe,
// coordonnées cliquables ; colonne droite = carte formulaire (crème foncé,
// filet 1px, padding 32px). Champs du handoff : Nom + Téléphone côte à côte,
// E-mail, sélecteur Professionnel / Particulier (deux boutons exclusifs),
// Votre demande. Inputs blancs, filet ink/18 %, focus = filet ink, aucun
// arrondi.
//
// Le prototype ne faisait que basculer un état local ; ici la demande PART
// réellement (même endpoint /contact-request que le reste du site, le choix
// Professionnel/Particulier replié en tête du message — pas de champ dédié
// côté serveur, en inventer un rendrait ces demandes invisibles dans
// l'admin), avec ce que le README demande pour la production : état « envoi
// en cours », message d'erreur réseau, pot de miel anti-spam, consentement
// RGPD explicite et `aria-describedby` reliant l'erreur au formulaire.
//
// Validation (handoff) : nom obligatoire ET au moins un moyen de contact
// (e-mail ou téléphone) ; sinon la note sous le bouton devient le message
// d'erreur. Succès : la carte est remplacée par « Merci. » + le texte de
// confirmation + « Envoyer une autre demande » (réinitialise l'état).
const INPUT =
  "w-full font-sans text-[14px] font-light text-ink bg-white border border-ink/[0.18] py-[13px] px-3.5 outline-none focus:border-ink transition-colors placeholder:text-ink/40";

const Field = ({ mode = "optional", label, children }) => {
  if (mode === "hidden") return null;
  return (
    <label className="grid gap-2">
      <span className={LABEL}>{label}</span>
      {children}
    </label>
  );
};

const EMPTY = { name: "", phone: "", email: "", message: "", website: "" };

export default function ContactFormSection({ props = {} }) {
  const nameMode = props.field_name || "required";
  const emailMode = props.field_email || "optional";
  const phoneMode = props.field_phone || "optional";
  const [form, setForm] = useState(EMPTY);
  const [who, setWho] = useState("pro");
  const [consent, setConsent] = useState(false);
  const [status, setStatus] = useState("idle"); // idle | sending | sent
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const needConsent = Boolean(props.consent_label);

  const submit = async (e) => {
    e.preventDefault();
    const name = nameMode === "hidden" ? "" : form.name.trim();
    const email = emailMode === "hidden" ? "" : form.email.trim();
    const phone = phoneMode === "hidden" ? "" : form.phone.trim();
    if ((nameMode === "required" && !name) || !(email || phone)) {
      setError(props.error_message || "Merci d'indiquer votre nom et un moyen de contact.");
      return;
    }
    if (needConsent && !consent) {
      setError(props.consent_label);
      return;
    }
    setError("");
    // Pot de miel : un champ invisible qu'un humain ne remplit jamais. Un
    // robot qui le remplit voit la confirmation, mais rien n'est envoyé.
    if (form.website) {
      setStatus("sent");
      return;
    }
    setStatus("sending");
    const whoLabel = who === "pro" ? (props.option_pro || "Professionnel") : (props.option_part || "Particulier");
    try {
      await api.post("/contact-request", {
        name,
        email,
        phone,
        message: `${props.label_who || "Vous êtes"} : ${whoLabel}\n\n${form.message.trim()}`,
        source: "pro_contact",
      });
      setStatus("sent");
      // Ce formulaire affiche sa propre confirmation au lieu de passer par
      // notifySent (ContactModalProvider), qui compte les demandes des
      // autres formulaires : le generate_lead Google doit donc partir ici.
      // Jamais pour le pot de miel ci-dessus, qui ne contacte pas le serveur.
      trackLead("pro_contact");
    } catch (err) {
      setStatus("idle");
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : props.network_error || "L'envoi a échoué. Réessayez.");
    }
  };

  const reset = (e) => {
    e.preventDefault();
    setForm(EMPTY);
    setWho("pro");
    setConsent(false);
    setError("");
    setStatus("idle");
  };

  const choice = (value, label) => (
    <button
      key={value}
      type="button"
      aria-pressed={who === value}
      onClick={() => setWho(value)}
      className={`text-[10px] tracking-[0.18em] uppercase border py-[11px] px-[18px] transition-colors ${
        who === value ? "bg-ink text-cream border-ink" : "bg-transparent text-ink border-ink/25 hover:border-ink"
      }`}
    >
      {label}
    </button>
  );

  return (
    <section id="contact" className="border-t border-ink/[0.12] scroll-mt-[90px]">
      <div className={`${CONTAINER} px-5 md:px-11 py-12 md:py-16`}>
      <div className="grid gap-10 md:gap-12 [grid-template-columns:repeat(auto-fit,minmax(280px,1fr))] items-start">
        <div>
          <RichText as="div" className={EYEBROW_LIGHT} html={props.eyebrow} />
          <RichText
            as="h2"
            className="block font-display font-light text-[clamp(26px,3vw,36px)] leading-[1.2] text-ink mt-[18px] mb-[22px]"
            html={props.heading}
          />
          <RichText as="div" preserveParagraphs className={`${BODY} mb-7`} html={props.body} />
          <div className="grid gap-3 text-[14px] font-light text-graphite">
            {props.phone && (
              <div className="flex gap-3.5 items-baseline">
                <span className="text-[10px] tracking-[0.2em] uppercase text-gold min-w-[64px]">Tél.</span>
                <a href={telHref(props.phone)} className="text-ink hover:text-ash" dir="ltr">{props.phone}</a>
              </div>
            )}
            {props.mobile && (
              <div className="flex gap-3.5 items-baseline">
                <span className="text-[10px] tracking-[0.2em] uppercase text-gold min-w-[64px]">Mobile</span>
                <a href={telHref(props.mobile)} className="text-ink hover:text-ash" dir="ltr">{props.mobile}</a>
              </div>
            )}
            {props.address && (
              <div className="flex gap-3.5 items-baseline">
                <span className="text-[10px] tracking-[0.2em] uppercase text-gold min-w-[64px]">Atelier</span>
                <span>{props.address}</span>
              </div>
            )}
          </div>
        </div>

        <div className="bg-sand border border-ink/[0.12] p-6 md:p-8">
          {status === "sent" ? (
            <div data-testid="contact-sent" className="min-h-[300px] flex flex-col justify-center gap-3.5" role="status">
              <div className="font-display font-light text-[30px] text-ink">{props.success_title || "Merci."}</div>
              <RichText
                as="div"
                preserveParagraphs
                className="text-[14px] leading-[1.8] font-light text-graphite"
                html={props.success_body}
              />
              {props.reset_label && (
                <a href="#contact" onClick={reset} className="text-[10px] tracking-[0.22em] uppercase text-ash hover:text-ink mt-2 w-fit">
                  {props.reset_label}
                </a>
              )}
            </div>
          ) : (
            <form data-testid="contact-form" onSubmit={submit} noValidate className="grid gap-[18px]" aria-describedby="contact-form-note">
              <div className="grid gap-[18px] [grid-template-columns:repeat(auto-fit,minmax(160px,1fr))]">
                <Field mode={nameMode} label={props.label_name || "Nom"}>
                  <input
                    type="text"
                    data-testid="contact-name"
                    autoComplete="name"
                    placeholder={props.placeholder_name || ""}
                    value={form.name}
                    onChange={set("name")}
                    className={INPUT}
                  />
                </Field>
                <Field mode={phoneMode} label={props.label_phone || "Téléphone"}>
                  <input
                    type="tel"
                    data-testid="contact-phone"
                    autoComplete="tel"
                    placeholder={props.placeholder_phone || ""}
                    value={form.phone}
                    onChange={set("phone")}
                    className={INPUT}
                  />
                </Field>
              </div>
              <Field mode={emailMode} label={props.label_email || "E-mail"}>
                <input
                  type="email"
                  data-testid="contact-email"
                  autoComplete="email"
                  placeholder={props.placeholder_email || ""}
                  value={form.email}
                  onChange={set("email")}
                  className={INPUT}
                />
              </Field>

              {/* Pot de miel — hors écran et hors tabulation, jamais affiché. */}
              <label className="absolute -left-[9999px] w-px h-px overflow-hidden" aria-hidden="true">
                Site web
                <input type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} />
              </label>

              <div className="grid gap-2.5">
                <span className={LABEL}>{props.label_who || "Vous êtes"}</span>
                <div className="flex flex-wrap gap-2.5" role="group" aria-label={props.label_who || "Vous êtes"}>
                  {choice("pro", props.option_pro || "Professionnel")}
                  {choice("part", props.option_part || "Particulier")}
                </div>
              </div>

              <label className="grid gap-2">
                <span className={LABEL}>{props.label_message || "Votre demande"}</span>
                <textarea
                  rows={5}
                  data-testid="contact-message"
                  placeholder={props.placeholder_message || ""}
                  value={form.message}
                  onChange={set("message")}
                  className={`${INPUT} leading-[1.7] resize-y`}
                />
              </label>

              {needConsent && (
                <label className="flex items-start gap-2.5 text-[11px] leading-[1.6] font-light text-graphite cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="contact-consent"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    className="mt-[3px] h-3.5 w-3.5 shrink-0 accent-[rgb(var(--c-ink,22_19_15))]"
                  />
                  <span>
                    {props.consent_label}
                    {props.consent_link_label && (
                      <>
                        {" "}
                        <SmartLink url={props.consent_url || "/mentions-legales"} className="underline underline-offset-2 text-ink hover:text-ash">
                          {props.consent_link_label}
                        </SmartLink>
                      </>
                    )}
                  </span>
                </label>
              )}

              <div className="flex items-center gap-[18px] flex-wrap">
                <button type="submit" data-testid="contact-submit" disabled={status === "sending"} className={`${BTN_FILL} disabled:opacity-60`}>
                  {status === "sending" ? (props.sending_label || "Envoi en cours…") : (props.submit_label || "Envoyer la demande")}
                </button>
                <span
                  id="contact-form-note"
                  role={error ? "alert" : undefined}
                  className={`text-[11px] font-light ${error ? "text-error" : "text-ash"}`}
                >
                  {error || props.note}
                </span>
              </div>
            </form>
          )}
        </div>
      </div>
      </div>
    </section>
  );
}
